import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Box } from "@mui/material";
import useTheme from "../../../../common/hooks/useTheme";
import { Theme } from "../../../../providers/ThemeProvider";
import { platformActions, RootStatePlatform } from "../../store/platform-slice";
import {
  isOppositeGenderSystem,
  useClientGender,
} from "../../hooks/useClientGender";
import ScrollableWrapper from "../common/ScrollableWrapper";
import MoreOptionsPopover from "../common/MoreOptionsPopover";
import ActionButton from "../../../../common/ui/ActionButton";
import CustomSelect from "../common/CustomSelect";
import SearchBar from "../common/SearchBar";
import FilterSelect from "../prescriptionTab/FilterSelect";
import ArrowDown from "../../svg/ArrowDown";
import { useScoreSidePanel } from "../score/ScoreSidePanelContext";
import BiomarkerRangeBar from "./BiomarkerRangeBar";
import TrendSparkline from "./TrendSparkline";
import {
  getBiomarkerBadge,
  getLatestEntry,
  hasRecordedValue,
} from "./biomarkerDisplay";
import {
  BiomarkerItem,
  enPrecisionMetric,
} from "../../../../common/entities/type";
import {
  enPrecisionTestKind,
  formatBiomarkerTabValue,
} from "../../store/helper";
import { formatDisplayDate } from "../../../../common/utils/date";

/**
 * "All biomarkers" view on the Health overview — every biomarker across all
 * systems with search / filters, grouped by test kind, one card per
 * biomarker. Clicking a card opens the biomarker side panel.
 *
 * The system dropdown drives the same redux `selectedSystem` the left-hand
 * system list uses, so selecting in either place keeps the two in sync
 * (design NOTE 16234:64510). The "N shown" count follows the active
 * filters/search (design NOTE 16288:72214).
 */
// Multi-select filters (FilterSelect, same as the client-list Score/Source
// filters): an empty selection means "no filter".
const STATUS_OPTIONS = [
  { key: "recorded", label: "Recorded" },
  { key: "not-recorded", label: "Not recorded" },
];
// Biomarkers have no Optimal category (the backend ranges stop at Normal), so
// the filter offers only the categories a biomarker can actually score.
const CATEGORY_OPTIONS = [
  { key: "2", label: "Normal" },
  { key: "1", label: "Borderline" },
  { key: "4", label: "Out of range" },
];
const GROUP_OPTIONS = ["By kind", "Alphabetical (A - Z)"] as const;

const AllBiomarkersView = () => {
  const theme: Theme = useTheme();
  const dispatch = useDispatch();
  const { openPanel, isOpen, contentType, userBiomarkerData } =
    useScoreSidePanel();
  const listRef = useRef<HTMLDivElement>(null);

  // Entering the view with a biomarker panel already open (the Manage-
  // biomarkers path from the system detail keeps the panel): bring the
  // selected card into view. Mount-only — while the view is open, cards are
  // selected by clicking them, so they are already visible.
  useEffect(() => {
    if (!isOpen || contentType !== "userBiomarker") return;
    listRef.current
      ?.querySelector("[data-selected='true']")
      ?.scrollIntoView({ block: "center" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<string[]>([]);
  const [kindFilter, setKindFilter] = useState<string[]>([]);
  const [categoryFilter, setCategoryFilter] = useState<string[]>([]);
  const [grouping, setGrouping] =
    useState<(typeof GROUP_OPTIONS)[number]>("By kind");

  const biomarkerData = useSelector(
    (state: RootStatePlatform) => state.platform.biomarkerData
  );
  const systems = useSelector(
    (state: RootStatePlatform) =>
      state.platform.dataPlatformServer.score?.systems
  );
  const selectedSystem = useSelector(
    (state: RootStatePlatform) => state.platform.selectedSystem
  );
  const gender = useClientGender();

  // Unique biomarkers across all metric groups.
  const allBiomarkers: BiomarkerItem[] = useMemo(() => {
    const byId = new Map<string, BiomarkerItem>();
    Object.values(biomarkerData || {}).forEach((group) => {
      (group as BiomarkerItem[]).forEach((item) => {
        if (!byId.has(item.biomarkerID)) byId.set(item.biomarkerID, item);
      });
    });
    return Array.from(byId.values());
  }, [biomarkerData]);

  // System dropdown options — same gender filtering as the left-hand rail,
  // but sourced from the union of the report's systems and the systems the
  // client actually has biomarkers for.
  //
  // The report omits a system entirely when it has no score for it, while the
  // biomarkers still exist: Body Composition Analysis (26) is missing from
  // `score.systems[]` for clients without a PNOE test, yet their InBody
  // results sit right there in the list below. Filtering by system must reach
  // every biomarker the list shows, so those systems are appended (in id
  // order) with their canonical name.
  const systemOptions = useMemo(() => {
    const reported = systems || [];
    const reportedIds = new Set(reported.map((s: any) => s.system));
    const dataOnlyIds = Object.keys(biomarkerData || {})
      .map(Number)
      .filter((id) => id > 0 && !reportedIds.has(id))
      .sort((a, b) => a - b);

    return [
      { title: "All systems", value: "0" },
      ...reported.map((s: any) => ({ id: s.system, title: s.name })),
      ...dataOnlyIds.map((id) => ({
        id,
        title: enPrecisionMetric[id] || `System ${id}`,
      })),
    ]
      .filter((opt: any) => !isOppositeGenderSystem(opt.id ?? 0, gender))
      .map(({ id, title }: any) => ({
        title,
        value: String(id ?? 0),
      }));
  }, [systems, biomarkerData, gender]);

  const kindOptions = useMemo(() => {
    const kinds = new Set<number>();
    allBiomarkers.forEach((b) => kinds.add(b.testKind));
    return Array.from(kinds)
      .sort((a, b) => a - b)
      .map((kind) => ({
        key: String(kind),
        label: enPrecisionTestKind[kind] || "Other",
      }));
  }, [allBiomarkers]);

  const filtered = useMemo(() => {
    return allBiomarkers.filter((item) => {
      if (
        selectedSystem !== 0 &&
        !(item.metrics || []).includes(selectedSystem)
      )
        return false;
      if (
        search.trim() !== "" &&
        !item.name.toLowerCase().includes(search.trim().toLowerCase())
      )
        return false;
      const recorded = hasRecordedValue(item);
      if (
        statusFilter.length > 0 &&
        !statusFilter.includes(recorded ? "recorded" : "not-recorded")
      )
        return false;
      if (kindFilter.length > 0 && !kindFilter.includes(String(item.testKind)))
        return false;
      if (categoryFilter.length > 0) {
        if (!recorded || !categoryFilter.includes(String(item.scoreCategory)))
          return false;
      }
      return true;
    });
  }, [
    allBiomarkers,
    selectedSystem,
    search,
    statusFilter,
    kindFilter,
    categoryFilter,
  ]);

  // Grouped sections (Group: By kind) or one alphabetical list.
  const sections = useMemo(() => {
    if (grouping === "Alphabetical (A - Z)") {
      return [
        {
          title: null as string | null,
          items: [...filtered].sort((a, b) => a.name.localeCompare(b.name)),
        },
      ];
    }
    const byKind = new Map<number, BiomarkerItem[]>();
    filtered.forEach((item) => {
      if (!byKind.has(item.testKind)) byKind.set(item.testKind, []);
      byKind.get(item.testKind)!.push(item);
    });
    return Array.from(byKind.entries())
      .sort((a, b) => a[0] - b[0])
      .map(([kind, items]) => ({
        title: enPrecisionTestKind[kind] || "Other",
        // Within each kind: recorded biomarkers first, then A–Z inside each
        // half.
        items: items.sort((a, b) => {
          const recordedDiff =
            Number(hasRecordedValue(b)) - Number(hasRecordedValue(a));
          if (recordedDiff !== 0) return recordedDiff;
          return a.name.localeCompare(b.name);
        }),
      }));
  }, [filtered, grouping]);

  const scopeText =
    selectedSystem === 0
      ? "all systems"
      : (systems || []).find((s: any) => s.system === selectedSystem)?.name ||
        enPrecisionMetric[selectedSystem] ||
        "selected system";

  return (
    <Box sx={styles.container(theme)}>
      <Box sx={styles.headerRow}>
        <Box>
          <Box sx={styles.title(theme)}>All biomarkers</Box>
          <Box sx={styles.subtitle(theme)}>
            Every biomarker across {scopeText} · {filtered.length} shown
          </Box>
        </Box>
      </Box>

      {/* CustomSelect's root stretches with flex:1, so pin its height here. */}
      <Box sx={{ flexShrink: 0, display: "flex" }}>
        <CustomSelect
          label="System"
          options={systemOptions}
          value={String(selectedSystem)}
          onChange={(value) => {
            const system = Number(value || 0);
            dispatch(platformActions.setSelectedSystem(system));
            dispatch(platformActions.setSelectedActiveConnection(0));
          }}
          // Page-level select: its dropdown must stay under the biomarker
          // side panel (z-index 1200), unlike selects inside modals.
          popperZIndex={1100}
        />
      </Box>

      <Box sx={styles.filtersRow}>
        <Box sx={{ flex: 1 }}>
          <SearchBar
            searchFilter={search}
            setSearchFilter={setSearch}
            placeholder="Search biomarkers"
            customHeight="34px"
            fontSize="14px"
          />
        </Box>
        <FilterSelect
          title="Status"
          options={STATUS_OPTIONS}
          selectedKeys={statusFilter}
          onSelectionChange={setStatusFilter}
          compact
        />
        <FilterSelect
          title="Test kind"
          options={kindOptions}
          selectedKeys={kindFilter}
          onSelectionChange={setKindFilter}
          compact
        />
        <FilterSelect
          title="Category"
          options={CATEGORY_OPTIONS}
          selectedKeys={categoryFilter}
          onSelectionChange={setCategoryFilter}
          compact
        />
        {/* Sort stays single-select: ActionButton trigger + MoreOptionsPopover
            menu, same composition as AddResultsMenu. */}
        <MoreOptionsPopover
          minWidth="160px"
          options={GROUP_OPTIONS.map((opt) => ({
            label: opt,
            onClick: () => setGrouping(opt),
          }))}
          renderTrigger={(open, toggle) => (
            <ActionButton
              label={`Group: ${grouping === "By kind" ? "Kind" : "A-Z"}`}
              iconRight={
                <Box
                  sx={{
                    display: "flex",
                    transition: "transform 0.2s ease",
                    transform: open ? "rotate(180deg)" : "rotate(0deg)",
                  }}
                >
                  <ArrowDown width={12} height={12} />
                </Box>
              }
              onClick={toggle}
              sx={{
                // Match the 34px / 12px filter controls, and pin the width so
                // swapping the Kind / A-Z label never resizes the button.
                height: "34px",
                fontSize: "12px",
                padding: "8px 12px",
                width: "124px",
                whiteSpace: "nowrap",
                ...(open && {
                  borderColor: theme.colors_ds.PRIMARY_ICON_MAIN,
                }),
              }}
            />
          )}
        />
      </Box>

      <Box ref={listRef} sx={styles.listWrapper}>
        <ScrollableWrapper customPadding="0">
          <Box sx={styles.sectionsColumn}>
            {sections.map((section, sIdx) => (
              <Box key={sIdx} sx={styles.section}>
                {section.title && (
                  <Box sx={styles.sectionTitle(theme)}>{section.title}</Box>
                )}
                <Box sx={styles.cardsGrid}>
                  {section.items.map((item) => (
                    <BiomarkerResultCard
                      key={item.biomarkerID}
                      item={item}
                      theme={theme}
                      systems={systems}
                      selected={
                        isOpen &&
                        contentType === "userBiomarker" &&
                        userBiomarkerData?.biomarkerID === item.biomarkerID
                      }
                      onClick={() =>
                        openPanel("userBiomarker", {
                          userBiomarkerData: {
                            biomarkerID: item.biomarkerID,
                          },
                        })
                      }
                    />
                  ))}
                </Box>
              </Box>
            ))}
            {filtered.length === 0 && (
              <Box sx={styles.emptyState(theme)}>
                No biomarkers match the current filters.
              </Box>
            )}
            <Box sx={{ height: "40px" }} />
          </Box>
        </ScrollableWrapper>
      </Box>
    </Box>
  );
};

// --- Biomarker card ---
const BiomarkerResultCard = ({
  item,
  theme,
  systems,
  selected,
  onClick,
}: {
  item: BiomarkerItem;
  theme: Theme;
  systems: any[] | undefined;
  selected: boolean;
  onClick: () => void;
}) => {
  const recorded = hasRecordedValue(item);
  const badge = recorded ? getBiomarkerBadge(item.scoreCategory, theme) : null;
  // Newest reading — for the date line only; the value comes from the item.
  const latest = getLatestEntry(item);

  const systemNames = Array.from(
    new Set(
      (item.metrics || [])
        .filter((m) => m !== 0)
        .map(
          (m) =>
            (systems || []).find((s: any) => s.system === m)?.name ||
            enPrecisionMetric[m]
        )
        .filter(Boolean)
    )
  );

  return (
    <Box
      sx={styles.card(theme, selected)}
      data-selected={selected ? "true" : undefined}
      onClick={onClick}
    >
      <Box sx={styles.cardHeader}>
        <Box sx={styles.cardName(theme)}>{item.name}</Box>
        <Box sx={styles.cardValueWrap}>
          {recorded && !item.qualitative && <TrendSparkline item={item} />}
          <Box sx={styles.cardValue(theme)}>
            {recorded
              ? formatBiomarkerTabValue(item, item.unit, item.qualitative)
              : `— ${item.unit || ""}`}
          </Box>
        </Box>
      </Box>
      {badge && (
        <Box sx={styles.badge(badge.background, badge.color)}>
          {badge.label}
        </Box>
      )}
      <BiomarkerRangeBar item={item} />
      {systemNames.length > 0 && (
        <Box sx={styles.systemsRow}>
          <Box component="span" sx={styles.systemsLabel(theme)}>
            System affected:
          </Box>
          {systemNames.map((name, idx) => (
            <Box component="span" key={idx} sx={styles.systemChip(theme)}>
              {name}
            </Box>
          ))}
        </Box>
      )}
      <Box sx={styles.kindRow(theme)}>
        {enPrecisionTestKind[item.testKind] || "Other"}
        {"  •  "}
        {latest ? formatDisplayDate(latest.dateCreated) : "Not recorded"}
      </Box>
    </Box>
  );
};

export default AllBiomarkersView;

const styles = {
  container: (theme: Theme) => ({
    flex: 1,
    display: "flex",
    flexDirection: "column" as const,
    minHeight: 0,
    gap: "16px",
    border: `1px solid ${theme.colors_ds.NEUTRAL_BORDER_WEAK}`,
    backgroundColor: theme.colors_ds.NEUTRAL_BG_MAIN,
    borderRadius: "8px",
    padding: "16px",
  }),
  headerRow: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: "16px",
  },
  title: (theme: Theme) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "16px",
    lineHeight: "22px",
    color: theme.colors_ds.NEUTRAL_TEXT_STRONG,
  }),
  subtitle: (theme: Theme) => ({
    fontFamily: "var(--font-Regular)",
    fontSize: "12px",
    lineHeight: "18px",
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
  }),
  filtersRow: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  listWrapper: {
    flex: 1,
    display: "flex",
    flexDirection: "column" as const,
    minHeight: 0,
  },
  sectionsColumn: {
    display: "flex",
    flexDirection: "column" as const,
    gap: "20px",
  },
  section: {
    display: "flex",
    flexDirection: "column" as const,
    gap: "12px",
  },
  sectionTitle: (theme: Theme) => ({
    fontFamily: "var(--font-Regular)",
    fontSize: "13px",
    lineHeight: "18px",
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
  }),
  // Fixed two columns: the shell's content width is fixed, and the grid sits
  // ~5px under the 3-column threshold of minmax(340px). Safari with overlay
  // scrollbars drops the 8px gutter and crossed it, showing 3 narrow columns
  // (and an empty slot with only 2 results).
  cardsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: "16px",
  },
  // Selected treatment mirrors SystemDetails' bloodBiomarkerCard: primary
  // border + soft shadow while the biomarker's side panel is open.
  card: (theme: Theme, selected: boolean) => ({
    display: "flex",
    flexDirection: "column" as const,
    gap: "10px",
    border: `1px solid ${
      selected
        ? theme.colors_ds.PRIMARY_ICON_MAIN
        : theme.colors_ds.NEUTRAL_BORDER_WEAK
    }`,
    borderRadius: "8px",
    padding: "16px",
    cursor: "pointer",
    backgroundColor: theme.colors_ds.NEUTRAL_BG_MAIN,
    boxShadow: selected
      ? "0px 8px 20px 0px rgba(0, 0, 0, 0.06), 0px 2px 8px 0px rgba(0, 0, 0, 0.04)"
      : "none",
    "&:hover": {
      borderColor: selected
        ? theme.colors_ds.PRIMARY_ICON_MAIN
        : theme.colors_ds.NEUTRAL_BORDER_DEFAULT,
    },
  }),
  cardHeader: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: "12px",
  },
  // Name and value share the header and both wrap: the value (with its
  // sparkline) takes at most half the width, the name the rest.
  cardName: (theme: Theme) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "14px",
    lineHeight: "20px",
    color: theme.colors_ds.NEUTRAL_TEXT_STRONG,
    flex: "1 1 auto",
    minWidth: 0,
    overflowWrap: "anywhere" as const,
  }),
  cardValueWrap: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "flex-end",
    gap: "8px",
    // Natural width, never squeezed by the name — it wraps only once it
    // reaches half the header.
    flex: "0 0 auto",
    maxWidth: "50%",
  },
  // Breaks between words only — its min-content width is the longest word,
  // so it can never be squeezed into a one-letter column.
  cardValue: (theme: Theme) => ({
    fontFamily: "var(--font-Bold)",
    fontSize: "14px",
    lineHeight: "20px",
    color: theme.colors_ds.NEUTRAL_TEXT_STRONG,
    textAlign: "right" as const,
    // Short values ("20 U/L", "5.09 x10⁶/µL") always stay on one line.
    minWidth: "88px",
  }),
  badge: (background: string, color: string) => ({
    alignSelf: "flex-start",
    fontFamily: "var(--font-Medium)",
    fontSize: "12px",
    lineHeight: "16px",
    padding: "2px 8px",
    borderRadius: "4px",
    backgroundColor: background,
    color,
  }),
  systemsRow: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    flexWrap: "wrap" as const,
  },
  systemsLabel: (theme: Theme) => ({
    fontFamily: "var(--font-Regular)",
    fontSize: "12px",
    lineHeight: "16px",
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
  }),
  systemChip: (theme: Theme) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "12px",
    lineHeight: "16px",
    padding: "2px 8px",
    borderRadius: "4px",
    backgroundColor: theme.colorTokens.N[30],
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
  }),
  kindRow: (theme: Theme) => ({
    fontFamily: "var(--font-Regular)",
    fontSize: "12px",
    lineHeight: "16px",
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
    whiteSpace: "pre" as const,
  }),
  emptyState: (theme: Theme) => ({
    border: `1px dashed ${theme.colors_ds.NEUTRAL_BORDER_DEFAULT}`,
    borderRadius: "8px",
    padding: "32px",
    textAlign: "center" as const,
    fontFamily: "var(--font-Regular)",
    fontSize: "13px",
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
  }),
};
